import React, { useEffect, useState } from 'react';
import { getProducts, getContacts, createPurchaseOrder, updateProductMOQ, updateProductionStatus, getProductsDetails, updateItemVisibility } from '../../services/zohoApi';
import { 
    saveDraftPurchaseOrder, 
    getDraftByVendor, 
    prepareDraftData
} from '../../services/adminPurchaseOrderApi';
import FavoriteButton from '../FavoriteButton';

const AdminStockOrderSection = () => {
    // Low stock threshold (editable if needed)
    const LOW_STOCK_THRESHOLD = 5;
    
    // State management
    const [stockFilter, setStockFilter] = useState('all'); // 'all', 'out', 'low'
    const [quantities, setQuantities] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [products, setProducts] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [selectedVendor, setSelectedVendor] = useState('all');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [selected, setSelected] = useState([]);
    const [editingMOQ, setEditingMOQ] = useState({}); // Track which products are being edited
    const [tempMOQ, setTempMOQ] = useState({}); // Store temporary MOQ values during editing
    const [editingETA, setEditingETA] = useState({}); // Track which products ETA are being edited
    const [tempETA, setTempETA] = useState({}); // Store temporary ETA values during editing
    const [productDetails, setProductDetails] = useState({}); // Store detailed product info with locations
    
    // Purchase order distribution states
    const [allCustomers, setAllCustomers] = useState([]);
    const [selectedCustomers, setSelectedCustomers] = useState([]);
    const [distributionPercentages, setDistributionPercentages] = useState({}); // {customerId: {itemId: percentage}}
    const [createdSalesOrders, setCreatedSalesOrders] = useState({}); // Track created SOs per customer
    
    // Options ETA disponibles (selon les vraies valeurs Zoho)
    const etaOptions = [
        { value: '', label: 'Not in production' },
        { value: 'Not available yet', label: 'Not available yet' },
        { value: 'ETA (+6 weeks)', label: 'ETA (+6 weeks)' },
        { value: 'ETA (+9 weeks)', label: 'ETA (+9 weeks)' },
        { value: 'ETA (+12 weeks)', label: 'ETA (+12 weeks)' },
        { value: 'ETA (+14 weeks)', label: 'ETA (+14 weeks)' },
        { value: 'ETA (+16 weeks)', label: 'ETA (+16 weeks)' }
    ];
    
    // Persistance temps réel avec debouncing
    const [draftSaving, setDraftSaving] = useState(false);
    const [lastSaveTime, setLastSaveTime] = useState(null);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [pendingOrderData, setPendingOrderData] = useState(null);
    const [notes, setNotes] = useState('');

    // Helper function to check if a product is favorite
    const isProductFavorite = (product) => {
        return product?.cf_favorite === "true" || product?.cf_favorite === true || 
               product?.custom_field_hash?.cf_favorite === "true" || 
               product?.custom_field_hash?.cf_favorite === true;
    };

    // Handle favorite changes
    const handleFavoriteChange = (itemId, isFavorite) => {
        setProducts(prevProducts => 
            prevProducts.map(product => 
                product.item_id === itemId 
                    ? { ...product, cf_favorite: isFavorite ? "true" : "false" }
                    : product
            )
        );
    };

    // Handle visibility changes
    const handleVisibilityChange = async (itemId, currentVisibility) => {
        try {
            const newVisibility = !currentVisibility;
            // Optimistic update
            setProducts(prevProducts => 
                prevProducts.map(product => 
                    product.item_id === itemId 
                        ? { ...product, is_visible: newVisibility }
                        : product
                )
            );
            
            await updateItemVisibility(itemId, newVisibility);
        } catch (err) {
            console.error('Error updating visibility:', err);
            // Revert on error
            setProducts(prevProducts => 
                prevProducts.map(product => 
                    product.item_id === itemId 
                        ? { ...product, is_visible: currentVisibility }
                        : product
                )
            );
            setError('Failed to update visibility');
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    // Charger le draft automatiquement quand un vendor spécifique est sélectionné
    useEffect(() => {
        if (selectedVendor && selectedVendor !== 'all') {
            loadDraftForVendor(selectedVendor);
        } else {
            // Réinitialiser la sélection si "tous les vendors"
            setSelected([]);
            setQuantities({});
            setNotes('');
        }
    }, [selectedVendor]);

    // Sauvegarder automatiquement lors des changements avec debouncing
    useEffect(() => {
        const saveDraftWithDelay = async () => {
            if (selectedVendor && selectedVendor !== 'all' && (selected.length > 0 || Object.keys(quantities).length > 0)) {
                try {
                    setDraftSaving(true);
                    
                    const vendor = vendors.find(v => v.contact_id === selectedVendor);
                    if (!vendor) return;
                    
                    const selectedProducts = products.filter(p => selected.includes(p.item_id));
                    
                    const draftData = prepareDraftData(
                        selectedVendor,
                        vendor.contact_name,
                        selectedProducts,
                        quantities,
                        notes
                    );
                    
                    await saveDraftPurchaseOrder(draftData);
                    setLastSaveTime(new Date().toLocaleTimeString());} catch (error) {
                    console.error('❌ Error auto-saving draft:', error);
                } finally {
                    setDraftSaving(false);
                }
            }
        };

        // Debounce : attendre 1.5 secondes après le dernier changement
        const timeoutId = setTimeout(saveDraftWithDelay, 1500);
        return () => clearTimeout(timeoutId);
    }, [selected, quantities, notes, selectedVendor, vendors, products]);

    const loadDraftForVendor = async (vendorId) => {
        try {const draft = await getDraftByVendor(vendorId);
            
            if (draft) {// Restaurer la sélection
                const productIds = draft.line_items.map(item => item.item_id);
                setSelected(productIds);
                
                // Restaurer les quantités
                const restoredQuantities = {};
                draft.line_items.forEach(item => {
                    restoredQuantities[item.item_id] = item.quantity;
                });
                setQuantities(restoredQuantities);
                
                // Restaurer les notes
                setNotes(draft.notes || '');} else {}
        } catch (error) {
            console.error('❌ Error loading vendor draft:', error);
        }
    };

    const loadData = async () => {
        setIsLoading(true);
        try {
            // Load products and vendor contacts in parallel
            const [productList, vendorList, customerList] = await Promise.all([
                getProducts(),
                getContacts({ contact_type: 'vendor' }),
                getContacts({ contact_type: 'customer' })
            ]);
            
            // Enrich products with location details for those with stock
            const productsWithStock = productList.filter(p => (p.stock_on_hand || 0) > 0);
            
            if (productsWithStock.length > 0) {
                try {
                    const itemIds = productsWithStock.map(p => p.item_id);
                    const locationDetails = await getProductsDetails(itemIds);
                    setProductDetails(locationDetails);
                } catch (err) {
                    console.warn('Failed to load bulk locations:', err.message);
                }
            }
            
            setProducts(productList || []);
            setVendors(vendorList || []);
            setAllCustomers(customerList || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    // Filter products by search
    let filteredProducts = products.filter(p => p.status === 'active');
    
    if (searchTerm.trim().length > 0) {
        const term = searchTerm.toLowerCase();
        filteredProducts = filteredProducts.filter(p =>
            p.name.toLowerCase().includes(term) ||
            (p.sku && p.sku.toLowerCase().includes(term))
        );
    }

    // Group products by supplier
    const groupProductsByVendor = () => {
        if (selectedVendor === 'all') {
            // Group all products by their associated vendors
            const groups = {};
            
            // For now, since Zoho doesn't have direct product-vendor relationships in the API response,
            // we'll group by a mock relationship or create groups based on vendors
            vendors.forEach(vendor => {
                // For demo purposes, distribute products across vendors
                // In real implementation, this would be based on actual product-vendor relationships
                const vendorProducts = filteredProducts.filter((_, index) => 
                    index % vendors.length === vendors.findIndex(v => v.contact_id === vendor.contact_id)
                );
                if (vendorProducts.length > 0) {
                    groups[vendor.contact_name] = vendorProducts;
                }
            });
            
            // If no vendors or no products distributed, put all in "Unassigned"
            if (Object.keys(groups).length === 0 && filteredProducts.length > 0) {
                groups['Unassigned Products'] = filteredProducts;
            }
            
            return groups;
        }
        
        const selectedVendorObj = vendors.find(v => v.contact_id === selectedVendor);
        const vendorName = selectedVendorObj ? selectedVendorObj.contact_name : 'Unknown Vendor';
        
        return { [vendorName]: filteredProducts };
    };

    const groupedProducts = groupProductsByVendor();

    // Flatten all products for compatibility with existing functions
    let allProducts = Object.values(groupedProducts).flat();
    
    // Apply stock filtering to all products
    if (stockFilter === 'out') {
        allProducts = allProducts.filter(p => (p.stock_on_hand || 0) === 0);
    } else if (stockFilter === 'low') {
        allProducts = allProducts.filter(p => 
            (p.stock_on_hand || 0) > 0 && (p.stock_on_hand || 0) <= LOW_STOCK_THRESHOLD
        );
    } else if (stockFilter === 'in') {
        allProducts = allProducts.filter(p => (p.stock_on_hand || 0) > 0);
    } else if (stockFilter === 'favorites') {
        allProducts = allProducts.filter(p => isProductFavorite(p));
    }
    
    // Regroup filtered products by vendor
    const filteredGroupedProducts = {};
    Object.keys(groupedProducts).forEach(vendorName => {
        const vendorProducts = groupedProducts[vendorName].filter(p => allProducts.includes(p));
        if (vendorProducts.length > 0) {
            filteredGroupedProducts[vendorName] = vendorProducts;
        }
    });
    
    const displayedProducts = allProducts;
    
    const availableProducts = filteredProducts.filter(p => (p.stock_on_hand || 0) > 0);
    const totalProducts = filteredProducts.length;
    const percentInStock = totalProducts === 0 ? '0%' : `${Math.round((availableProducts.length / totalProducts) * 100)}%`;

    // Get vendor for a product (based on current grouping logic)
    const getProductVendor = (productId) => {
        for (const [vendorName, vendorProducts] of Object.entries(filteredGroupedProducts)) {
            if (vendorProducts.some(p => p.item_id === productId)) {
                // Find the actual vendor object
                const vendor = vendors.find(v => v.contact_name === vendorName);
                return vendor || { contact_id: 'unassigned', contact_name: vendorName };
            }
        }
        return null;
    };

    // Check if all selected products belong to the same vendor
    const getSelectedProductsVendorValidation = () => {
        if (selected.length === 0) {
            return { isValid: false, message: 'No products selected' };
        }

        const selectedProducts = selected.map(id => {
            const vendor = getProductVendor(id);
            return { productId: id, vendor };
        });

        // Get unique vendors
        const uniqueVendors = [...new Set(selectedProducts.map(p => p.vendor?.contact_id).filter(Boolean))];
        
        if (uniqueVendors.length === 0) {
            return { isValid: false, message: 'Selected products have no assigned vendor' };
        }
        
        if (uniqueVendors.length > 1) {
            const vendorNames = [...new Set(selectedProducts.map(p => p.vendor?.contact_name).filter(Boolean))];
            return { 
                isValid: false, 
                message: `This selection contains products from multiple suppliers: ${vendorNames.join(', ')}. Please select products from a single supplier.` 
            };
        }

        const primaryVendor = selectedProducts[0].vendor;
        return { 
            isValid: true, 
            vendor: primaryVendor,
            message: `Ready to create purchase order for ${primaryVendor.contact_name}` 
        };
    };

    // Get vendor validation for selected products
    const vendorValidation = getSelectedProductsVendorValidation();

    const handleSelect = (id) => {
        if (selected.includes(id)) {
            setSelected(prev => prev.filter(x => x !== id));
            // Supprimer aussi la quantité associée
            setQuantities(prev => {
                const newQuantities = { ...prev };
                delete newQuantities[id];
                return newQuantities;
            });
        } else {
            setSelected([...selected, id]);
        }
    };

    const handleQuantityChange = (id, value) => {
        const qty = Math.max(0, parseInt(value) || 0);
        setQuantities(prev => ({ ...prev, [id]: qty }));
    };

    // Extract MOQ from product custom fields
    const getMOQ = (product) => {
        return product.cf_moq ? parseInt(product.cf_moq) : 1;
    };

    // MOQ editing functions
    const startEditMOQ = (productId, currentMOQ) => {
        setEditingMOQ(prev => ({ ...prev, [productId]: true }));
        setTempMOQ(prev => ({ ...prev, [productId]: currentMOQ }));
    };

    const cancelEditMOQ = (productId) => {
        setEditingMOQ(prev => ({ ...prev, [productId]: false }));
        setTempMOQ(prev => ({ ...prev, [productId]: undefined }));
    };

    const saveEditMOQ = async (productId, itemId) => {
        try {
            const newMOQ = tempMOQ[productId];
            if (newMOQ === undefined || newMOQ < 1) {
                alert('MOQ must be at least 1');
                return;
            }

            await updateProductMOQ(itemId, newMOQ);
            
            // Update local product data
            setProducts(prev => prev.map(p => 
                p.item_id === itemId ? { ...p, cf_moq: newMOQ } : p
            ));
            
            // Clear editing state
            setEditingMOQ(prev => ({ ...prev, [productId]: false }));
            setTempMOQ(prev => ({ ...prev, [productId]: undefined }));} catch (error) {
            console.error('Error updating MOQ:', error);
            alert('Failed to update MOQ: ' + error.message);
        }
    };

    // Extract ETA from product custom fields
    const getETA = (product) => {
        return product.cf_in_production_1 || '';
    };

    // ETA editing functions
    const startEditETA = (productId, currentETA) => {
        setEditingETA(prev => ({ ...prev, [productId]: true }));
        setTempETA(prev => ({ ...prev, [productId]: currentETA }));
    };

    const cancelEditETA = (productId) => {
        setEditingETA(prev => ({ ...prev, [productId]: false }));
        setTempETA(prev => ({ ...prev, [productId]: undefined }));
    };

    const saveEditETA = async (productId, itemId) => {
        try {
            const newETA = tempETA[productId];
            
            await updateProductionStatus(itemId, newETA || '');
            
            // Update local product data
            setProducts(prev => prev.map(p => 
                p.item_id === itemId ? { ...p, cf_in_production_1: newETA || '' } : p
            ));
            
            // Clear editing state
            setEditingETA(prev => ({ ...prev, [productId]: false }));
            setTempETA(prev => ({ ...prev, [productId]: undefined }));} catch (error) {
            console.error('Error updating ETA:', error);
            alert('Failed to update ETA: ' + error.message);
        }
    };

    const handleSelectAll = () => {
        setSelected(displayedProducts.map(p => p.item_id));
    };

    const handleClearSelection = () => {
        setSelected([]);
    };

    const handleCreatePurchaseOrder = async () => {
        const validation = getSelectedProductsVendorValidation();
        
        if (!validation.isValid) {
            alert(validation.message);
            return;
        }

        try {
            const selectedProducts = displayedProducts.filter(p => selected.includes(p.item_id));
            const lineItems = selectedProducts.map(product => ({
                item_id: product.item_id,
                name: product.name,
                description: product.description || '',
                quantity: quantities[product.item_id] || 1,
                rate: product.purchase_rate || 0
            }));

            const purchaseOrderData = {
                vendor_id: validation.vendor.contact_id,
                vendor_name: validation.vendor.contact_name,
                line_items: lineItems,
                date: new Date().toISOString().split('T')[0],
                reference_number: `PO-${Date.now()}`,
                notes: notes || `Created from stock management interface for ${validation.vendor.contact_name}`,
                useCustomQuantities: true
            };

            // Afficher la popup de confirmation au lieu d'envoyer directement
            setPendingOrderData(purchaseOrderData);
            setShowConfirmationModal(true);
        } catch (err) {
            alert('Error preparing purchase order: ' + err.message);
        }
    };

    const handleCreatePurchaseOrderWithMinQty = async () => {
        const validation = getSelectedProductsVendorValidation();
        
        if (!validation.isValid) {
            alert(validation.message);
            return;
        }

        try {
            const selectedProducts = displayedProducts.filter(p => selected.includes(p.item_id));
            const lineItems = selectedProducts.map(product => ({
                item_id: product.item_id,
                name: product.name,
                description: product.description || '',
                quantity: getMOQ(product), // Use MOQ from custom field
                rate: product.purchase_rate || 0
            }));

            const purchaseOrderData = {
                vendor_id: validation.vendor.contact_id,
                vendor_name: validation.vendor.contact_name,
                line_items: lineItems,
                date: new Date().toISOString().split('T')[0],
                reference_number: `PO-MIN-${Date.now()}`,
                notes: notes || `Created from stock management interface with minimum vendor quantities for ${validation.vendor.contact_name}`,
                useCustomQuantities: false
            };

            // Afficher la popup de confirmation au lieu d'envoyer directement
            setPendingOrderData(purchaseOrderData);
            setShowConfirmationModal(true);
        } catch (err) {
            alert('Error preparing purchase order with min qty: ' + err.message);
        }
    };

    // Distribution management functions
    const handleCustomerSelection = (customerId, isSelected) => {
        if (isSelected) {
            setSelectedCustomers(prev => [...prev, customerId]);
            // Initialize distribution percentages for this customer
            if (pendingOrderData) {
                const initialPercentages = {};
                pendingOrderData.line_items.forEach(item => {
                    initialPercentages[item.item_id] = 0;
                });
                setDistributionPercentages(prev => ({
                    ...prev,
                    [customerId]: initialPercentages
                }));
            }
        } else {
            setSelectedCustomers(prev => prev.filter(id => id !== customerId));
            // Remove distribution percentages for this customer
            setDistributionPercentages(prev => {
                const newPercentages = { ...prev };
                delete newPercentages[customerId];
                return newPercentages;
            });
        }
    };

    const handleDistributionChange = (customerId, itemId, percentage) => {
        const numValue = Math.max(0, Math.min(100, parseFloat(percentage) || 0));
        setDistributionPercentages(prev => ({
            ...prev,
            [customerId]: {
                ...prev[customerId],
                [itemId]: numValue
            }
        }));
    };

    const createSalesOrderForCustomer = async (customerId) => {
        if (!pendingOrderData || !distributionPercentages[customerId]) {
            alert('No distribution data available for this customer');
            return;
        }

        try {
            const customer = allCustomers.find(c => c.contact_id === customerId);
            if (!customer) {
                alert('Customer not found');
                return;
            }

            // Calculate line items based on distribution percentages
            const salesOrderLineItems = pendingOrderData.line_items
                .filter(item => distributionPercentages[customerId][item.item_id] > 0)
                .map(item => {
                    const percentage = distributionPercentages[customerId][item.item_id];
                    const distributedQuantity = Math.ceil((item.quantity * percentage) / 100);
                    
                    return {
                        item_id: item.item_id,
                        quantity: distributedQuantity,
                        rate: item.rate * 1.2, // Add 20% markup for sales price
                        description: `From PO ${pendingOrderData.reference_number} - ${percentage}% distribution`
                    };
                });

            if (salesOrderLineItems.length === 0) {
                alert('No items with distribution percentage > 0 for this customer');
                return;
            }

            const salesOrderData = {
                customer_id: customerId,
                line_items: salesOrderLineItems,
                reference_number: `SO-${pendingOrderData.reference_number}-${customer.contact_name.replace(/\s+/g, '').slice(0, 6)}`,
                notes: `Sales order generated from purchase order ${pendingOrderData.reference_number} for ${customer.contact_name}`
            };

            // Create sales order via API
            const response = await fetch('/api/zoho-inventory/sales-orders', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(salesOrderData)
            });

            if (!response.ok) {
                throw new Error(`Failed to create sales order: ${response.statusText}`);
            }

            const result = await response.json();
            
            // Track created sales order
            setCreatedSalesOrders(prev => ({
                ...prev,
                [customerId]: {
                    salesorder_id: result.salesorder_id,
                    salesorder_number: result.salesorder_number,
                    created_at: new Date().toISOString()
                }
            }));

            alert(`Sales order ${result.salesorder_number} created successfully for ${customer.contact_name}!`);
        } catch (error) {
            console.error('Error creating sales order:', error);
            alert(`Error creating sales order: ${error.message}`);
        }
    };

    const getTotalDistributionForItem = (itemId) => {
        let total = 0;
        selectedCustomers.forEach(customerId => {
            if (distributionPercentages[customerId] && distributionPercentages[customerId][itemId]) {
                total += distributionPercentages[customerId][itemId];
            }
        });
        return total;
    };

    const confirmSendToZoho = async () => {
        if (!pendingOrderData) return;

        try {
            await createPurchaseOrder(pendingOrderData);
            alert(`Purchase order created successfully for ${pendingOrderData.vendor_name}!`);
            
            // Réinitialiser l'état après envoi réussi
            setSelected([]);
            setQuantities({});
            setNotes('');
            setShowConfirmationModal(false);
            setPendingOrderData(null);
            // Reset distribution state
            setSelectedCustomers([]);
            setDistributionPercentages({});
            setCreatedSalesOrders({});
        } catch (err) {
            alert('Error creating purchase order: ' + err.message);
        }
    };

    const cancelSendToZoho = () => {
        setShowConfirmationModal(false);
        setPendingOrderData(null);
        // Reset distribution state
        setSelectedCustomers([]);
        setDistributionPercentages({});
        setCreatedSalesOrders({});
    };

    if (isLoading) {
        return (
            <div className="admin-stock-section">
                <div className="loading-spinner">Loading products and suppliers...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="admin-stock-section">
                <div className="error">Error: {error}</div>
                <button onClick={loadData}>Retry</button>
            </div>
        );
    }

    return (
        <div className="admin-stock-section">
            <h2>Stock Management & Supplier Orders</h2>
            
            {/* Statistics */}
            <div className="stock-stats">
                <div className="stat-card">
                    <h3>Total Products</h3>
                    <p>{totalProducts}</p>
                </div>
                <div className="stat-card">
                    <h3>In Stock</h3>
                    <p>{availableProducts.length} ({percentInStock})</p>
                </div>
                <div className="stat-card">
                    <h3>Out of Stock</h3>
                    <p>{filteredProducts.filter(p => (p.stock_on_hand || 0) === 0).length}</p>
                </div>
                <div className="stat-card">
                    <h3>Low Stock</h3>
                    <p>{filteredProducts.filter(p => 
                        (p.stock_on_hand || 0) > 0 && (p.stock_on_hand || 0) <= LOW_STOCK_THRESHOLD
                    ).length}</p>
                </div>
            </div>

            {/* Filters and controls */}
            <div className="filters-section">
                <div className="filters-row">
                    <div className="filter-group">
                        <label>Stock Filter</label>
                        <select
                            value={stockFilter}
                            onChange={(e) => setStockFilter(e.target.value)}
                        >
                            <option value="all">All Products</option>
                            <option value="favorites">⭐ Favorites</option>
                            <option value="in">In Stock</option>
                            <option value="out">Out of Stock</option>
                            <option value="low">Low Stock</option>
                        </select>
                    </div>

                    <div className="filter-group">
                        <label>Supplier</label>
                        <select
                            value={selectedVendor}
                            onChange={(e) => setSelectedVendor(e.target.value)}
                        >
                            <option value="all">All Suppliers</option>
                            {vendors.map(vendor => (
                                <option key={vendor.contact_id} value={vendor.contact_id}>
                                    {vendor.contact_name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group">
                        <label>Search Product</label>
                        <input
                            type="text"
                            placeholder="Search product name or SKU..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {/* Section persistance temps réel */}
                {selectedVendor && selectedVendor !== 'all' && (
                    <div className="draft-status-section" style={{
                        background: '#f8f9fa',
                        padding: '15px',
                        borderRadius: '8px',
                        border: '1px solid #e9ecef',
                        marginTop: '15px'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <h4 style={{ margin: 0, color: '#495057' }}>
                                Draft for {vendors.find(v => v.contact_id === selectedVendor)?.contact_name}
                            </h4>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                {draftSaving && (
                                    <span style={{ color: '#007bff', fontSize: '14px' }}>💾 Saving...</span>
                                )}
                                {lastSaveTime && (
                                    <span style={{ color: '#6c757d', fontSize: '14px' }}>
                                        Last saved: {lastSaveTime}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Notes:</label>
                                <textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Add notes for this purchase order..."
                                    style={{
                                        width: '100%',
                                        minHeight: '60px',
                                        padding: '8px',
                                        border: '1px solid #ced4da',
                                        borderRadius: '4px',
                                        fontSize: '14px'
                                    }}
                                />
                            </div>
                            <div style={{ 
                                background: 'white', 
                                padding: '10px', 
                                borderRadius: '6px',
                                border: '1px solid #dee2e6',
                                minWidth: '120px',
                                textAlign: 'center'
                            }}>
                                <div style={{ fontSize: '14px', color: '#6c757d', marginBottom: '5px' }}>Selected</div>
                                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#28a745' }}>
                                    {selected.length} products
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="actions-row">
                    <button className="btn btn-secondary" onClick={handleSelectAll} disabled={displayedProducts.length === 0}>
                        Select All
                    </button>
                    <button className="btn btn-secondary" onClick={handleClearSelection} disabled={selected.length === 0}>
                        Clear Selection
                    </button>
                    <button 
                        className="btn btn-primary"
                        onClick={handleCreatePurchaseOrder} 
                        disabled={!vendorValidation.isValid}
                        title={vendorValidation.message}
                    >
                        Create Purchase Order ({selected.length})
                    </button>
                    <button 
                        className="btn btn-success"
                        onClick={handleCreatePurchaseOrderWithMinQty} 
                        disabled={!vendorValidation.isValid}
                        title={vendorValidation.message}
                    >
                        Create PO with Min Vendor Qty ({selected.length})
                    </button>
                    {!vendorValidation.isValid && selected.length > 0 && (
                        <div style={{ 
                            color: '#ff6b35', 
                            fontSize: '12px', 
                            fontWeight: '500',
                            marginTop: '4px',
                            maxWidth: '400px' 
                        }}>
                            ⚠️ {vendorValidation.message}
                        </div>
                    )}
                    {vendorValidation.isValid && (
                        <div style={{ 
                            color: '#4caf50', 
                            fontSize: '12px', 
                            fontWeight: '500',
                            marginTop: '4px' 
                        }}>
                            ✅ {vendorValidation.message}
                        </div>
                    )}
                </div>
            </div>

            {/* Products table */}
            <div className="products-table-container">
                <table className="products-table">
                    <thead>
                        <tr>
                            <th>
                                <input
                                    type="checkbox"
                                    checked={selected.length === displayedProducts.length && displayedProducts.length > 0}
                                    onChange={handleSelectAll}
                                />
                            </th>
                            <th>Fav</th>
                            <th>Vis</th>
                            <th>Product</th>
                            <th>SKU</th>
                            <th>Stock</th>
                            <th>MOQ</th>
                            <th>Production ETA</th>
                            <th>Quantity</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Object.entries(filteredGroupedProducts).map(([vendorName, vendorProducts]) => (
                            <React.Fragment key={vendorName}>
                                <tr className="vendor-section-header">
                                    <td colSpan="9">
                                        📦 {vendorName} ({vendorProducts.length} products)
                                    </td>
                                </tr>
                                {vendorProducts.map(product => {
                                    // Debug: Log des custom fields
                                    if (product.custom_field_hash && Object.keys(product.custom_field_hash).length > 0) {}
                                    
                                    return (
                                    <tr key={product.item_id} className={selected.includes(product.item_id) ? 'selected' : ''}>
                                        <td>
                                            <input
                                                type="checkbox"
                                                checked={selected.includes(product.item_id)}
                                                onChange={() => handleSelect(product.item_id)}
                                            />
                                        </td>
                                        <td style={{ width: '40px', textAlign: 'center' }}>
                                            <FavoriteButton 
                                                product={product} 
                                                onFavoriteChange={handleFavoriteChange}
                                            />
                                        </td>
                                        <td style={{ width: '40px', textAlign: 'center' }}>
                                            <button
                                                onClick={() => handleVisibilityChange(product.item_id, product.is_visible !== false)}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    fontSize: '1.2em',
                                                    opacity: product.is_visible !== false ? 1 : 0.5
                                                }}
                                                title={product.is_visible !== false ? "Visible on public site" : "Hidden from public site"}
                                            >
                                                {product.is_visible !== false ? '👁️' : '🚫'}
                                            </button>
                                        </td>
                                        <td>
                                            <div>
                                                <strong>{product.name}</strong>
                                                {product.description && <div className="product-description">{product.description}</div>}
                                            </div>
                                        </td>
                                        <td>{product.sku || 'N/A'}</td>
                                        <td>
                                            <div>
                                                <span className={`stock-status ${(product.stock_on_hand || 0) === 0 ? 'out-of-stock' : 
                                                    (product.stock_on_hand || 0) <= LOW_STOCK_THRESHOLD ? 'low-stock' : 'in-stock'}`}>
                                                    {product.stock_on_hand || 0}
                                                </span>
                                                {/* Affichage automatique des locations si disponibles */}
                                                {productDetails[product.item_id] && productDetails[product.item_id].locations && (
                                                    <div style={{
                                                        marginTop: '6px',
                                                        fontSize: '11px',
                                                        color: '#666',
                                                        lineHeight: '1.3'
                                                    }}>
                                                        {productDetails[product.item_id].locations.map((location, index) => (
                                                            <div key={index} style={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                                padding: '2px 0',
                                                                borderLeft: '2px solid #e0e0e0',
                                                                paddingLeft: '6px',
                                                                marginBottom: '2px'
                                                            }}>
                                                                <span style={{ 
                                                                    fontWeight: '500',
                                                                    color: '#555',
                                                                    maxWidth: '120px',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap'
                                                                }}>
                                                                    {location.location_name || 'Unknown'}
                                                                </span>
                                                                <span style={{
                                                                    fontWeight: '600',
                                                                    color: (location.location_stock_on_hand || 0) > 0 ? '#4caf50' : '#f44336',
                                                                    backgroundColor: (location.location_stock_on_hand || 0) > 0 ? '#e8f5e8' : '#ffeaea',
                                                                    padding: '1px 4px',
                                                                    borderRadius: '3px',
                                                                    fontSize: '10px'
                                                                }}>
                                                                    {location.location_stock_on_hand || 0}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            {editingMOQ[product.item_id] ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={tempMOQ[product.item_id] || getMOQ(product)}
                                                        onChange={(e) => setTempMOQ(prev => ({ 
                                                            ...prev, 
                                                            [product.item_id]: parseInt(e.target.value) || 1 
                                                        }))}
                                                        className="moq-input"
                                                    />
                                                    <div className="moq-actions">
                                                        <button
                                                            onClick={() => saveEditMOQ(product.item_id, product.item_id)}
                                                            className="moq-save-btn"
                                                            title="Save MOQ"
                                                        >
                                                            ✓
                                                        </button>
                                                        <button
                                                            onClick={() => cancelEditMOQ(product.item_id)}
                                                            className="moq-cancel-btn"
                                                            title="Cancel"
                                                        >
                                                            ✕
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    <span style={{
                                                        display: 'inline-block',
                                                        padding: '4px 8px',
                                                        background: getMOQ(product) > 1 ? '#e3f2fd' : '#f5f5f5',
                                                        color: getMOQ(product) > 1 ? '#1976d2' : '#666',
                                                        borderRadius: '4px',
                                                        fontSize: '12px',
                                                        fontWeight: '600'
                                                    }}>
                                                        {getMOQ(product)}
                                                    </span>
                                                    <button
                                                        onClick={() => startEditMOQ(product.item_id, getMOQ(product))}
                                                        className="edit-moq-btn"
                                                        title="Edit MOQ"
                                                    >
                                                        ✏️
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            {editingETA[product.item_id] ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                    <select
                                                        value={tempETA[product.item_id] || getETA(product)}
                                                        onChange={(e) => setTempETA(prev => ({ 
                                                            ...prev, 
                                                            [product.item_id]: e.target.value 
                                                        }))}
                                                        style={{ minWidth: '120px' }}
                                                    >
                                                        {etaOptions.map(option => (
                                                            <option key={option.value} value={option.value}>
                                                                {option.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <div style={{ display: 'flex', gap: '2px' }}>
                                                        <button
                                                            onClick={() => saveEditETA(product.item_id, product.item_id)}
                                                            style={{
                                                                background: '#4CAF50',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '3px',
                                                                padding: '4px 6px',
                                                                cursor: 'pointer',
                                                                fontSize: '12px'
                                                            }}
                                                            title="Save ETA"
                                                        >
                                                            ✓
                                                        </button>
                                                        <button
                                                            onClick={() => cancelEditETA(product.item_id)}
                                                            style={{
                                                                background: '#f44336',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '3px',
                                                                padding: '4px 6px',
                                                                cursor: 'pointer',
                                                                fontSize: '12px'
                                                            }}
                                                            title="Cancel"
                                                        >
                                                            ✕
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <span style={{
                                                        display: 'inline-block',
                                                        padding: '4px 8px',
                                                        background: getETA(product) ? '#e8f5e8' : '#f5f5f5',
                                                        color: getETA(product) ? '#2e7d32' : '#666',
                                                        borderRadius: '4px',
                                                        fontSize: '11px',
                                                        fontWeight: '600',
                                                        minWidth: '100px',
                                                        textAlign: 'center'
                                                    }}>
                                                        {etaOptions.find(opt => opt.value === getETA(product))?.label || getETA(product) || 'Not in production'}
                                                    </span>
                                                    <button
                                                        onClick={() => startEditETA(product.item_id, getETA(product))}
                                                        style={{
                                                            background: 'transparent',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            marginLeft: '5px',
                                                            padding: '2px 4px',
                                                            borderRadius: '3px'
                                                        }}
                                                        title="Edit Production ETA"
                                                    >
                                                        ✏️
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <input
                                                type="number"
                                                min="0"
                                                value={quantities[product.item_id] || ''}
                                                onChange={(e) => handleQuantityChange(product.item_id, e.target.value)}
                                                placeholder="Qty"
                                            />
                                        </td>
                                    </tr>
                                    );
                                })}
                            </React.Fragment>
                        ))}
                        
                        {Object.values(filteredGroupedProducts).flat().length === 0 && (
                            <tr>
                                <td colSpan="8" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                                    No products found with selected filters
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Modal de confirmation pour envoi à Zoho avec distribution */}
            {showConfirmationModal && pendingOrderData && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        background: 'white',
                        padding: '30px',
                        borderRadius: '12px',
                        maxWidth: '900px',
                        width: '95%',
                        maxHeight: '90vh',
                        overflow: 'auto',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
                    }}>
                        <h3 style={{ marginTop: 0, color: '#495057' }}>
                            🔄 Purchase Order & Distribution Setup
                        </h3>
                        
                        <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
                            <div style={{ flex: 1 }}>
                                <strong>Vendor:</strong> {pendingOrderData.vendor_name}
                            </div>
                            <div style={{ flex: 1 }}>
                                <strong>Reference:</strong> {pendingOrderData.reference_number}
                            </div>
                        </div>

                        {/* Customer Selection Section */}
                        <div style={{ marginBottom: '25px' }}>
                            <h4 style={{ margin: '0 0 15px 0', color: '#495057' }}>
                                📋 Select Customers for Distribution
                            </h4>
                            <div style={{ 
                                maxHeight: '150px', 
                                overflow: 'auto', 
                                border: '1px solid #dee2e6',
                                borderRadius: '6px',
                                padding: '15px',
                                background: '#f8f9fa'
                            }}>
                                {allCustomers.map(customer => (
                                    <label key={customer.contact_id} style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        marginBottom: '10px',
                                        cursor: 'pointer',
                                        padding: '8px',
                                        borderRadius: '4px',
                                        background: selectedCustomers.includes(customer.contact_id) ? '#e3f2fd' : 'transparent'
                                    }}>
                                        <input
                                            type="checkbox"
                                            checked={selectedCustomers.includes(customer.contact_id)}
                                            onChange={(e) => handleCustomerSelection(customer.contact_id, e.target.checked)}
                                            style={{ marginRight: '10px' }}
                                        />
                                        <span style={{ fontWeight: '500' }}>{customer.contact_name}</span>
                                        {customer.company_name && (
                                            <span style={{ marginLeft: '8px', color: '#6c757d', fontSize: '14px' }}>
                                                ({customer.company_name})
                                            </span>
                                        )}
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Distribution Matrix */}
                        {selectedCustomers.length > 0 && (
                            <div style={{ marginBottom: '25px' }}>
                                <h4 style={{ margin: '0 0 15px 0', color: '#495057' }}>
                                    📊 Distribution Percentages by Customer
                                </h4>
                                <div style={{ 
                                    border: '1px solid #dee2e6',
                                    borderRadius: '6px',
                                    overflow: 'hidden'
                                }}>
                                    <table style={{ width: '100%', fontSize: '14px' }}>
                                        <thead style={{ background: '#f8f9fa' }}>
                                            <tr>
                                                <th style={{ padding: '12px', textAlign: 'left', minWidth: '200px' }}>Product</th>
                                                <th style={{ padding: '12px', textAlign: 'center' }}>PO Qty</th>
                                                {selectedCustomers.map(customerId => {
                                                    const customer = allCustomers.find(c => c.contact_id === customerId);
                                                    return (
                                                        <th key={customerId} style={{ padding: '12px', textAlign: 'center', minWidth: '120px' }}>
                                                            {customer?.contact_name?.slice(0, 12)}...
                                                        </th>
                                                    );
                                                })}
                                                <th style={{ padding: '12px', textAlign: 'center' }}>Total %</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pendingOrderData.line_items.map((item, index) => (
                                                <tr key={index} style={{ borderBottom: '1px solid #f1f3f4' }}>
                                                    <td style={{ padding: '12px' }}>
                                                        <div style={{ fontWeight: '600' }}>{item.name}</div>
                                                        {item.sku && (
                                                            <div style={{ fontSize: '12px', color: '#6c757d' }}>
                                                                SKU: {item.sku}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>
                                                        {item.quantity}
                                                    </td>
                                                    {selectedCustomers.map(customerId => (
                                                        <td key={customerId} style={{ padding: '12px', textAlign: 'center' }}>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                max="100"
                                                                value={distributionPercentages[customerId]?.[item.item_id] || ''}
                                                                onChange={(e) => handleDistributionChange(customerId, item.item_id, e.target.value)}
                                                                style={{
                                                                    width: '60px',
                                                                    padding: '4px',
                                                                    border: '1px solid #ccc',
                                                                    borderRadius: '4px',
                                                                    textAlign: 'center'
                                                                }}
                                                                placeholder="0"
                                                            />
                                                            <span style={{ fontSize: '12px', marginLeft: '2px' }}>%</span>
                                                        </td>
                                                    ))}
                                                    <td style={{ 
                                                        padding: '12px', 
                                                        textAlign: 'center',
                                                        fontWeight: '600',
                                                        color: getTotalDistributionForItem(item.item_id) === 100 ? '#28a745' : 
                                                               getTotalDistributionForItem(item.item_id) > 100 ? '#dc3545' : '#fd7e14'
                                                    }}>
                                                        {getTotalDistributionForItem(item.item_id).toFixed(1)}%
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Sales Order Generation Section */}
                        {selectedCustomers.length > 0 && (
                            <div style={{ marginBottom: '25px' }}>
                                <h4 style={{ margin: '0 0 15px 0', color: '#495057' }}>
                                    🎯 Generate Sales Orders
                                </h4>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px' }}>
                                    {selectedCustomers.map(customerId => {
                                        const customer = allCustomers.find(c => c.contact_id === customerId);
                                        const hasDistribution = pendingOrderData.line_items.some(item => 
                                            distributionPercentages[customerId]?.[item.item_id] > 0
                                        );
                                        const createdOrder = createdSalesOrders[customerId];
                                        
                                        return (
                                            <div key={customerId} style={{
                                                border: '1px solid #dee2e6',
                                                borderRadius: '6px',
                                                padding: '15px',
                                                background: createdOrder ? '#d4edda' : '#f8f9fa'
                                            }}>
                                                <div style={{ fontWeight: '600', marginBottom: '10px' }}>
                                                    {customer?.contact_name}
                                                </div>
                                                
                                                {createdOrder ? (
                                                    <div style={{ color: '#155724' }}>
                                                        ✅ Sales Order Created
                                                        <div style={{ fontSize: '14px', marginTop: '5px' }}>
                                                            <strong>SO #:</strong> {createdOrder.salesorder_number}
                                                        </div>
                                                        <div style={{ fontSize: '12px', color: '#6c757d' }}>
                                                            Created: {new Date(createdOrder.created_at).toLocaleString()}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => createSalesOrderForCustomer(customerId)}
                                                        disabled={!hasDistribution}
                                                        style={{
                                                            width: '100%',
                                                            padding: '10px',
                                                            border: 'none',
                                                            background: hasDistribution ? '#007bff' : '#6c757d',
                                                            color: 'white',
                                                            borderRadius: '4px',
                                                            cursor: hasDistribution ? 'pointer' : 'not-allowed',
                                                            fontSize: '14px',
                                                            fontWeight: '500'
                                                        }}
                                                        title={hasDistribution ? 'Create Sales Order' : 'Set distribution percentages first'}
                                                    >
                                                        📄 Create Sales Order
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Original Purchase Order Summary */}
                        <div style={{ marginBottom: '20px' }}>
                            <h4 style={{ margin: '0 0 15px 0', color: '#495057' }}>
                                📦 Purchase Order Summary
                            </h4>
                            <div style={{ 
                                maxHeight: '200px', 
                                overflow: 'auto', 
                                border: '1px solid #dee2e6',
                                borderRadius: '6px'
                            }}>
                                <table style={{ width: '100%', fontSize: '14px' }}>
                                    <thead style={{ background: '#f8f9fa' }}>
                                        <tr>
                                            <th style={{ padding: '8px', textAlign: 'left' }}>Product</th>
                                            <th style={{ padding: '8px', textAlign: 'center' }}>Qty</th>
                                            <th style={{ padding: '8px', textAlign: 'right' }}>Rate</th>
                                            <th style={{ padding: '8px', textAlign: 'right' }}>Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pendingOrderData.line_items.map((item, index) => (
                                            <tr key={index} style={{ borderBottom: '1px solid #f1f3f4' }}>
                                                <td style={{ padding: '8px' }}>
                                                    <div style={{ fontWeight: '600' }}>{item.name}</div>
                                                    {item.sku && (
                                                        <div style={{ fontSize: '12px', color: '#6c757d' }}>
                                                            SKU: {item.sku}
                                                        </div>
                                                    )}
                                                </td>
                                                <td style={{ padding: '8px', textAlign: 'center' }}>
                                                    {item.quantity}
                                                </td>
                                                <td style={{ padding: '8px', textAlign: 'right' }}>
                                                    ${item.rate.toFixed(2)}
                                                </td>
                                                <td style={{ padding: '8px', textAlign: 'right' }}>
                                                    ${(item.quantity * item.rate).toFixed(2)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot style={{ background: '#f8f9fa', fontWeight: 'bold' }}>
                                        <tr>
                                            <td colSpan="3" style={{ padding: '8px', textAlign: 'right' }}>
                                                Total:
                                            </td>
                                            <td style={{ padding: '8px', textAlign: 'right' }}>
                                                ${pendingOrderData.line_items.reduce((sum, item) => 
                                                    sum + (item.quantity * item.rate), 0
                                                ).toFixed(2)}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                        
                        {pendingOrderData.notes && (
                            <div style={{ marginBottom: '20px' }}>
                                <strong>Notes:</strong>
                                <div style={{ 
                                    padding: '10px', 
                                    background: '#f8f9fa', 
                                    borderRadius: '6px',
                                    marginTop: '5px',
                                    fontSize: '14px'
                                }}>
                                    {pendingOrderData.notes}
                                </div>
                            </div>
                        )}
                        
                        <div style={{ 
                            background: '#fff3cd', 
                            border: '1px solid #ffeaa7',
                            borderRadius: '6px',
                            padding: '12px',
                            marginBottom: '20px'
                        }}>
                            <strong>⚠️ Important:</strong> The purchase order will be sent to Zoho Inventory. 
                            Sales orders for selected customers can be generated independently.
                        </div>
                        
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <button
                                onClick={cancelSendToZoho}
                                style={{
                                    padding: '10px 20px',
                                    border: '1px solid #6c757d',
                                    background: 'white',
                                    color: '#6c757d',
                                    borderRadius: '6px',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmSendToZoho}
                                style={{
                                    padding: '10px 20px',
                                    border: 'none',
                                    background: '#28a745',
                                    color: 'white',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontWeight: '600'
                                }}
                            >
                                ✅ Send Purchase Order to Zoho
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminStockOrderSection;
